import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCanManageProducts } from '@/hooks/useVisibleProducts'

interface SettingCard {
  title: string
  description: string
  icon: React.ReactNode
  href: string
  requireOwner?: boolean
  requireProductAdmin?: boolean
}

export default function SettingsPage() {
  const { t } = useTranslation('settings')
  const { user } = useAuth()

  const isOwner = user?.platform_role === 'owner'
  const canManageProducts = useCanManageProducts()
  const isLocalUser = user?.type === 'local'

  const settingCards: SettingCard[] = [
    {
      title: t('language.title'),
      description: t('language.description'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
      href: '/settings/language'
    },
    {
      title: t('profile.title'),
      description: t('profile.description'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      href: '/settings/profile'
    },
    {
      title: t('password.title'),
      description: t('password.description'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      ),
      href: '/settings/password',
      requireOwner: false
    },
    {
      title: t('products.title', 'Products'),
      description: t('products.description', 'Choose which products this site shows'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      href: '/settings/products',
      requireProductAdmin: true
    }
  ]

  const visibleCards = settingCards.filter(card => {
    // Check owner requirement
    if (card.requireOwner && !isOwner) return false
    if (card.requireProductAdmin && !canManageProducts) return false
    // Check local user requirement for password
    if (card.href === '/settings/password' && !isLocalUser) return false
    return true
  })

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('title', 'Settings')}</h1>
        <p className="text-gray-600 mt-2">{t('description', 'Manage your account settings and preferences')}</p>
      </div>

      {/* Settings Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visibleCards.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            className="group bg-white rounded-xl shadow-lg border border-gray-200 p-6 transition-all duration-200 hover:shadow-xl hover:border-primary hover:-translate-y-1"
          >
            <div className="flex items-start space-x-4">
              <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-200">
                {card.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors duration-200">
                  {card.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {card.description}
                </p>
              </div>
              <div className="shrink-0">
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
